import { Check, MapPin, Building2, Globe2 } from "lucide-react";
import { useLang } from "../lib/i18n";
import { inr } from "../lib/api";

export const PLAN_STYLE = {
  emerald: { head: "bg-gradient-to-r from-emerald-500 to-emerald-400", ring: "border-emerald-500/30", soft: "bg-emerald-500/10", text: "text-emerald-400", icon: MapPin, glow: "shadow-[0_0_30px_rgba(16,185,129,0.15)]" },
  blue: { head: "bg-gradient-to-r from-blue-600 to-blue-500", ring: "border-blue-500/30", soft: "bg-blue-500/10", text: "text-blue-400", icon: Building2, glow: "shadow-[0_0_30px_rgba(59,130,246,0.15)]" },
  purple: { head: "bg-gradient-to-r from-purple-600 to-purple-500", ring: "border-purple-500/30", soft: "bg-purple-500/10", text: "text-purple-400", icon: Globe2, glow: "shadow-[0_0_30px_rgba(139,92,246,0.15)]" },
};

export function PlanCard({ plan, onPay, current, payLabel, disabled, upgradeFrom }) {
  const { t, lang } = useLang();
  const s = PLAN_STYLE[plan.color] || PLAN_STYLE.blue;
  const Icon = s.icon;
  const feats = (lang === "hi" ? plan.features_hi : plan.features_en) || [];
  const diff = upgradeFrom ? plan.price - upgradeFrom.price : null;
  return (
    <div className={`glass flex flex-col overflow-hidden rounded-[2rem] transition-all duration-300 hover:-translate-y-2 ${s.ring} ${s.glow}`} data-testid={`plan-card-${plan.code}`}>
      <div className={`${s.head} flex items-center gap-3 px-6 py-5 text-white`}>
        <div className="rounded-full bg-white/20 p-2 backdrop-blur-sm"><Icon size={20} /></div>
        <h3 className="text-lg font-bold tracking-wide">{t(plan.name_en, plan.name_hi)}</h3>
      </div>
      <div className="flex flex-1 flex-col p-8">
        <p className="text-5xl font-black tracking-tighter text-white" data-testid={`plan-price-${plan.code}`}>{inr(plan.price)}</p>
        <p className="mt-2 text-xs font-bold uppercase tracking-widest text-slate-500">{t("One-time activation fee • GST shown at checkout", "एक बार का एक्टिवेशन शुल्क • GST चेकआउट पर")}</p>
        <div className={`mt-6 rounded-2xl ${s.soft} p-5`}>
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400">{t("Maximum daily survey reward limit", "अधिकतम दैनिक सर्वे रिवॉर्ड सीमा")}</p>
          <p className={`mt-2 text-3xl font-black ${s.text}`} data-testid={`plan-cap-${plan.code}`}>
            {lang === "hi" ? `${inr(plan.daily_cap)} तक` : `up to ${inr(plan.daily_cap)}`}
          </p>
          <p className="mt-2 text-[11px] font-medium text-slate-500">{t("A maximum limit — not guaranteed earnings.", "यह अधिकतम सीमा है — गारंटीशुदा कमाई नहीं।")}</p>
        </div>
        <ul className="mt-8 flex-1 space-y-4">
          {feats.map((f) => (
            <li key={f} className="flex gap-3 text-sm font-medium text-slate-300"><Check size={18} className="mt-0.5 shrink-0 text-[#D4AF37]" />{f}</li>
          ))}
        </ul>
        {diff !== null && diff > 0 && (
          <p className="mt-6 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-xs font-medium text-slate-400" data-testid={`plan-upgrade-diff-${plan.code}`}>
            {t("Upgrade difference", "अपग्रेड अंतर")}: <b className="text-white">{inr(diff)}</b> + GST
          </p>
        )}
        {onPay && (
          <button
            onClick={() => onPay(plan)}
            disabled={disabled || current}
            data-testid={`plan-pay-now-${plan.code}`}
            className={`mt-8 w-full rounded-full px-5 py-4 text-sm font-bold text-white transition-all disabled:cursor-not-allowed disabled:opacity-50 ${s.head} hover:brightness-110 hover:shadow-lg`}
          >
            {current ? t("Current plan", "वर्तमान प्लान") : payLabel || "Pay Now / अभी भुगतान करें"}
          </button>
        )}
      </div>
    </div>
  );
}
